import { DbStatus, User, UserActionSuccessTypeEnum } from '@sleipai/sleip-models';
import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {
  selectAllUsersReducer,
  State,
  userFeatureKey,
} from '../reducers/user.reducer';
import * as fromRouterSelector from '../../router-store/router.selectors';
import {
  ClientWebAppProjectEnum,
  ClientWebAppProjectType,
} from '../../env-configuration';
import { PAGE_SIZE, QueryConfig } from '../../helpers/query-config';
import { dynamicSort } from '../../helpers';

export const selectUserState = createFeatureSelector<State>(userFeatureKey);

export const isLoading = (state: State): boolean => state.loading;
export const isLoaded = (state: State): boolean => state.loaded;
export const totalUserCount = (state: State): number => state.totalUserCount;
export const numberOfPages = (state: State): number => state.numberOfPages;
export const userSaved = (state: State): boolean => state.userSaved;
export const userError = (state: State): string | null => state.error;
export const userActionSuccessType = (
  state: State
): UserActionSuccessTypeEnum | null => state.userActionSuccessType;

export const selectIsLoading: MemoizedSelector<object, boolean> =
  createSelector(selectUserState, isLoading);

export const selectIsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectUserState,
  isLoaded
);

export const selectUserSaved: MemoizedSelector<object, boolean> =
  createSelector(selectUserState, userSaved);

export const selectUserError: MemoizedSelector<object, string | null> =
  createSelector(selectUserState, userError);

export const selectTotalUserCount: MemoizedSelector<object, number> =
  createSelector(selectUserState, totalUserCount);

export const selectNumberOfPages: MemoizedSelector<object, number> =
  createSelector(selectUserState, numberOfPages);

export const selectUsersHasMore: MemoizedSelector<object, boolean> =
  createSelector(selectUserState, (state: State) => state.hasMoreUsers);

export const selectUserLastSearchIds: MemoizedSelector<object, string[] | null> =
  createSelector(selectUserState, (state: State) => state.lastSearchIds);

export const selectAllUsers = createSelector(
  selectUserState,
  selectAllUsersReducer
);

export const selectUserActionSuccessType = createSelector(
  selectUserState,
  userActionSuccessType
);

export const selectSingleUser = (uid: string) =>
  createSelector(selectAllUsers, (users: User[]) => {
    if (users) {
      return users.find((user: User) => user.uid === uid) ?? null;
    } else {
      return null;
    }
  });

export const selectGroupUsers = (groupId: string, appAccess: string) =>
  createSelector(selectAllUsers, (users: User[]) => {
    if (users) {
      return users.filter(
        (user) =>
          user.group &&
          user.group.id === groupId &&
          user.appAccess === appAccess
      );
    } else {
      return null;
    }
  });

export const selectFilterUser = (
  sort: string,
  direction: string,
  page: number,
  pageSize: number
) =>
  createSelector(selectAllUsers, (users: User[]) => {
    if (users && users.length > 0) {
      const sorted = users.sort(dynamicSort(sort, direction));
      sorted.slice(page * pageSize, (page + 1) * pageSize);
      return sorted;
    }
    return null;
  });

export const selectUsersByIds = (ids: string[]) =>
  createSelector(selectAllUsers, (users: User[]) => {
    if (users) {
      return users.filter(({ uid }) => ids.indexOf(uid ?? '') != -1);
    } else {
      return null;
    }
  });

export const selectUserHorseLoadStatusById = createSelector(
  selectAllUsers,
  fromRouterSelector.selectRouteNestedParam('userId'),
  (users: User[], userId: string) => {
    if (users && userId) {
      return (
        users.find((user: User) => user.uid === userId)?.horsesLoadStatus ??
        false
      );
    } else {
      return false;
    }
  }
);

export const selectUserBySearchTerm = (searchTerm: string, userType: string) =>
  createSelector(selectAllUsers, (users: User[]) => {
    if (users) {
      if (userType) {
        return users.filter(
          (user) =>
            user.name.includes(searchTerm) && user.appAccess === userType
        );
      } else {
        return users.filter((user) => user.name.includes(searchTerm));
      }
    }
    return [];
  });

export const selectAdminUsers = createSelector(
  selectAllUsers,
  (users: User[]) => {
    if (!users || users.length === 0) {
      return [];
    }
    return users.filter((user) => user.admin === true);
  }
);

export const selectUsersListPage = (
  queryConfig: QueryConfig,
  clientWebAppProject?: ClientWebAppProjectType
) => {
  const {
    page,
    orderBy = 'name',
    orderDirection = 'asc',
    pageSize = PAGE_SIZE,
    searchQuery,
    filterMap,
  } = queryConfig;

  const hasActiveSearch = !!(searchQuery && searchQuery.trim());
  const hasActiveFilters = !!(filterMap && Array.from(filterMap.entries()).some(([key, val]) => {
    if (key === 'dbStatus' && val === DbStatus.ACTIVE) {
      return false;
    }
    if (key === `${ClientWebAppProjectEnum.VET_WEB_APP}-filter`) {
      const arr = val ? Array.from(val as any[]) : [];
      return arr.length > 0 && !arr.includes('all');
    }
    return val !== undefined && val !== null && (Array.isArray(val) ? val.length > 0 : true);
  }));
  const isSearchOrFilterActive = hasActiveSearch || hasActiveFilters;

  return createSelector(
    selectAllUsers,
    selectUserLastSearchIds,
    selectIsLoading,
    (users: User[] | null | undefined, lastSearchIds: string[] | null, isLoading: boolean) => {
      // -- handle case of no users availble
      if (isLoading || !users || users.length === 0) {
        return [];
      }

      if (isSearchOrFilterActive && lastSearchIds) {
        const userMap = new Map(users.map(u => [u.id, u]));
        const ordered = lastSearchIds
          .map(id => userMap.get(id))
          .filter((u): u is User => !!u);
        return ordered;
      }

      let filteredUsers = [...users];
      // -- Filter start
      const filterByDbStatus = queryConfig.filterMap?.get('dbStatus') as string;
      if (filterByDbStatus) {
        filteredUsers = filteredUsers.filter(
          (user) => user.dbStatus === filterByDbStatus
        );
      }



      // 1. Filter out all active users exit out early if we don't have any active users.
      // if (clientWebAppProject !== ClientWebAppProjectEnum.VET_WEB_APP) {
      //   filteredUsers = filteredUsers.filter(
      //     (user) => user.dbStatus === DbStatus.ACTIVE
      //   );
      //   console.error('selectUsersListPage - filteredUsers', filteredUsers);
      //   if (!filteredUsers.length) return [];
      // }

      // 2. Filter by Search Query (if provided)
      if (searchQuery) {
        // Breakout regex and cleaned query for easier reading
        const cleanedQuery = searchQuery
          .trim()
          .replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        if (cleanedQuery) {
          const regex = new RegExp(cleanedQuery, 'gi');
          filteredUsers = filteredUsers.filter((user) => {
            // Check match for user neame
            const nameMatch = user.name && user.name.match(regex);
            // Check match for user owner name
            const emailMatch = user.email && user.email.match(regex);
            return nameMatch || emailMatch;
          });
          if (!filteredUsers.length) return [];
        }
      }

      // 3. filter for based on filterMap
      filterMap?.forEach((value: unknown, key: string) => {
        if (key === 'groupId') {
          filteredUsers = filteredUsers.filter(
            (user) => user.group?.id === value
          );
        }

        if (key === 'filterByUserObjectIds') {
          const userIdsToFilterUsers = value as string[];
          filteredUsers = filteredUsers.filter(
            (user) => user.id && userIdsToFilterUsers.includes(user.id)
          );
        }

        if (key === `${ClientWebAppProjectEnum.VET_WEB_APP}-filter`) {
          const filterValues = Array.from<string>(value as []);
          if (
            !filterValues.includes('all') &&
            filterValues.filter((filter) => filter !== DbStatus.REMOVED)
              .length > 0
          ) {
            filteredUsers = filteredUsers.filter((user) =>
              user.appAccess ? filterValues.includes(user.appAccess) : false
            );
          }
          if (filterValues.includes(DbStatus.REMOVED)) {
            filteredUsers = filteredUsers.filter(
              (user) => user.dbStatus === DbStatus.REMOVED
            );
          }
        }
      });

      // -- Start Sorting
      const sortedUsers = [...filteredUsers].sort(
        dynamicSort(orderBy, orderDirection)
      );

      // -- Start Pagination
      const endIndex = pageSize * (page + 1);
      return sortedUsers.slice(0, endIndex);
    }
  );
};
